import { forwardRef, ElementType, type ReactElement } from 'react';

import { BaseChip } from './BaseChip.js';
import { ChipSuffix, ChipPrefix } from './ChipContent.js';
import { ChipDeleteButton } from './ChipDeleteButton.js';
import { ChipKey } from './ChipKey.js';
import { GroupedChip } from './GroupedChip.js';
import type { ChipProps } from './types/chip.js';
import { useInternalChipGroupContext } from '../chip-group/hooks/useInternalChipGroupContext.js';

/**
 * Chip component
 */
const InternalChip: <E extends ElementType = 'div'>(
  props: ChipProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: ChipProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const { children } = props;
    const chipGroupContext = useInternalChipGroupContext();

    return chipGroupContext === undefined ? (
      <BaseChip ref={forwardedRef} {...props}>
        {children}
      </BaseChip>
    ) : (
      <GroupedChip ref={forwardedRef} {...props}>
        {children}
      </GroupedChip>
    );
  },
);

/**
 * Use the `Chip` to display compact labels that communicate the status, category, or condition of an item using semantic color and emphasis.
 * @public
 */
export const Chip = Object.assign(InternalChip, {
  Prefix: ChipPrefix,
  Suffix: ChipSuffix,
  Key: ChipKey,
  DeleteButton: ChipDeleteButton,
});

(Chip as typeof Chip & { displayName: string }).displayName = 'Chip';
